import { useCallback } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  useChatCheckpointRestore,
  useChatMessageBranching,
  type RewindChatThread,
  type SendBranchInlineEdit,
} from '@terra/ui-design-system';
import {
  redoChatCheckpointRestore,
  restoreChatCheckpoint,
  rewindChatHistory,
} from '../store/chatHistoryStore';
import { useCopilotBranchPreferencesStore } from '../store/copilotBranchPreferencesStore';

const rewindChatThread: RewindChatThread = async ({
  threadId,
  fromMessageId,
  revertFiles,
}) => {
  const response = await rewindChatHistory({
    threadId,
    fromMessageId,
    revertFiles,
  });

  return {
    history: response.history,
    branchedFromMessageId: response.branchedFromMessageId,
    removedMessageCount: response.removedMessageCount,
  };
};

export function useCopilotChatBranching({
  chatId,
  chatMessages,
  setMessages,
  onBranchApplied,
  abortInFlightTurn,
  sendInlineEdit,
}: {
  chatId: string;
  chatMessages: TerraUIMessage[];
  setMessages: (
    messages:
      | TerraUIMessage[]
      | ((messages: TerraUIMessage[]) => TerraUIMessage[]),
  ) => void;
  onBranchApplied: () => void;
  abortInFlightTurn: () => Promise<void>;
  sendInlineEdit: SendBranchInlineEdit;
}) {
  const submitPreference = useCopilotBranchPreferencesStore(
    (state) => state.submitPreference,
  );
  const setSubmitPreference = useCopilotBranchPreferencesStore(
    (state) => state.setSubmitPreference,
  );

  const branching = useChatMessageBranching({
    rewindChatThread,
    threadId: chatId,
    setMessages,
    onBranchApplied,
    abortInFlightTurn,
    submitPreference,
    onSubmitPreferenceChange: setSubmitPreference,
    sendInlineEdit,
  });

  const checkpoint = useChatCheckpointRestore({
    threadId: chatId,
    restoreCheckpoint: restoreChatCheckpoint,
    redoRestore: redoChatCheckpointRestore,
  });

  const { handleEditUserMessage, handleEditAskUserAnswer } = branching;

  const onEditUserMessage = useCallback(
    (messageId: string) => {
      handleEditUserMessage(messageId, chatMessages);
    },
    [chatMessages, handleEditUserMessage],
  );

  const onEditAskUserAnswer = useCallback(
    (args: { toolCallId?: string; toolCallPartId?: string }) => {
      handleEditAskUserAnswer({
        messages: chatMessages,
        ...args,
      });
    },
    [chatMessages, handleEditAskUserAnswer],
  );

  return {
    ...branching,
    checkpoint,
    onEditUserMessage,
    onEditAskUserAnswer,
  };
}
